import { motion } from 'framer-motion'
import { ExternalLink, Shield, Heart, Building2, ClipboardCheck, BookOpen } from 'lucide-react'
import SubpageHero from '@/components/common/SubpageHero'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/ui/Card'
import SEO from '@/components/common/SEO'
import { GOVERNMENT_RESOURCES, RESOURCE_CATEGORIES, type ResourceCategory } from '@/data/governmentResources'
import PromotionSlot from '@/components/common/PromotionSlot'

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

const categoryIcons: Record<ResourceCategory, typeof Shield> = {
  '등급신청': ClipboardCheck,
  '복지서비스': Heart,
  '정책제도': BookOpen,
  '시설평가': Building2,
  '치매': Shield,
}

export default function GuideResourcesPage() {
  return (
    <>
      <SEO
        title="정부/공공기관 안내"
        description="요양 관련 정부/공공기관 사이트를 한눈에 모았습니다. 장기요양등급 신청, 복지서비스, 시설 평가 정보를 확인하세요."
        url="/guide/resources"
      />
      <SubpageHero title="정부/공공기관 안내" description="요양 관련 공공기관 사이트 모음" icon={<Shield size={20} className="text-white" />} />
      <PageContainer>
        <motion.div
          className="flex flex-col gap-6"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        >
          {/* 소개 */}
          <motion.section variants={fadeUp}>
            <h1 className="text-h2 font-bold text-cb-gray-900">요양 관련 정부 기관 안내</h1>
            <p className="mt-2 text-body text-cb-gray-600 leading-relaxed">
              장기요양등급 신청부터 복지 혜택, 시설 평가까지 — 공신력 있는 정부/공공기관 사이트를 모았습니다.
            </p>
          </motion.section>

          {/* 카테고리별 그룹 */}
          {RESOURCE_CATEGORIES.map((cat) => {
            const resources = GOVERNMENT_RESOURCES.filter((r) => r.category === cat.category)
            if (resources.length === 0) return null
            const Icon = categoryIcons[cat.category]

            return (
              <motion.section key={cat.category} variants={fadeUp}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-cb-primary/10">
                    <Icon size={18} className="text-cb-primary" />
                  </div>
                  <div>
                    <h2 className="text-body font-bold text-cb-gray-900">{cat.label}</h2>
                    <p className="text-caption text-cb-gray-500">{cat.description}</p>
                  </div>
                </div>

                <div className="flex flex-col gap-2.5">
                  {resources.map((resource) => (
                    <a
                      key={resource.id}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Card className="hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-body-sm font-semibold text-cb-gray-800">{resource.name}</p>
                            <p className="mt-0.5 text-caption text-cb-gray-500">{resource.description}</p>
                          </div>
                          <ExternalLink size={16} className="text-cb-gray-400 flex-shrink-0" />
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>
              </motion.section>
            )
          })}

          {/* 안내 */}
          <motion.section variants={fadeUp}>
            <div className="rounded-xl bg-cb-gray-50 p-4">
              <p className="text-caption text-cb-gray-500 leading-relaxed">
                위 링크는 각 기관의 공식 웹사이트로 연결됩니다. 요양 관련 문의는 국민건강보험공단(1577-1000)으로 전화하시면 상담받으실 수 있습니다.
              </p>
            </div>
          </motion.section>

          {/* 프로모션 슬롯: guide_bottom */}
          <PromotionSlot slotKey="guide_bottom" className="mt-2" />
        </motion.div>
      </PageContainer>
    </>
  )
}
